import { type ManualApproveHandlerSchema } from 'modules/procurement/components/procurementTypes/Workflows/sidePanels/schemas/manualApproveHandler'
import { HandlerFormComponentPayload } from 'modules/workflows/contexts/WorkflowContext'
import { DEFAULT_DURATION_UNIT, isValidDateUnit } from 'modules/workflows/lib/durationUnit'

type EmployeeSegment = {
  name: string
  input?: { team_id?: number; role_id?: string } | Record<string, never>
}

const isEmployeeSegment = (segment: unknown): segment is EmployeeSegment => {
  return typeof segment === 'object' && segment !== null && 'name' in segment
}

const mapEmployeeSegments = (segments: unknown) => {
  if (!segments || !Array.isArray(segments)) {
    return undefined
  }

  const mapped = segments.filter(isEmployeeSegment).map((segment) => {
    if (segment.name === 'anyone_in_team' || segment.name === 'team_leads') {
      const teamId =
        typeof segment.input === 'object' && segment.input !== null && 'team_id' in segment.input
          ? Number(segment.input.team_id)
          : null

      if (teamId && teamId > 0) {
        if (segment.name === 'anyone_in_team') {
          return {
            name: 'anyone_in_team' as const,
            input: { team_id: teamId },
          }
        }
        return {
          name: 'team_leads' as const,
          input: { team_id: teamId },
        }
      }
    } else if (segment.name === 'employee_manager' || segment.name === 'admins') {
      if (segment.name === 'employee_manager') {
        return {
          name: 'employee_manager' as const,
          input: {},
        }
      }
      return {
        name: 'admins' as const,
        input: {},
      }
    } else if (segment.name === 'job_catalog_role') {
      const roleId =
        typeof segment.input === 'object' && segment.input !== null && 'role_id' in segment.input
          ? String(segment.input.role_id)
          : undefined

      if (roleId) {
        return {
          name: 'job_catalog_role' as const,
          input: { role_id: roleId },
        }
      }
    }
    return null
  })

  return mapped.filter((segment): segment is NonNullable<typeof segment> => segment !== null)
}

export const manualApproveNodeToSchemaMapper = (
  payload: HandlerFormComponentPayload
): ManualApproveHandlerSchema['nodeData']['payload'] => {
  const due_on_unit = String(payload.due_on_unit)
  const reviewer_ids = payload.reviewer_ids
  const employee_segments = payload.employee_segments
  const fallback_reviewer_ids = payload.fallback_reviewer_ids
  const fallback_employee_segments = payload.fallback_employee_segments
  const dueOnValue = Number(payload.due_on_value ?? 1)
  const durationUnit = isValidDateUnit(due_on_unit) ? due_on_unit : DEFAULT_DURATION_UNIT

  const filteredEmployeeSegments = mapEmployeeSegments(employee_segments)
  const filteredFallbackEmployeeSegments = mapEmployeeSegments(fallback_employee_segments)

  return {
    due_on_value: dueOnValue,
    due_on_unit: durationUnit,
    ...(reviewer_ids && Array.isArray(reviewer_ids) && reviewer_ids.length > 0
      ? { reviewer_ids: reviewer_ids.map((id) => Number(id)) }
      : {}),
    ...(filteredEmployeeSegments && filteredEmployeeSegments.length > 0
      ? { employee_segments: filteredEmployeeSegments }
      : {}),
    ...(fallback_reviewer_ids &&
    Array.isArray(fallback_reviewer_ids) &&
    fallback_reviewer_ids.length > 0
      ? { fallback_reviewer_ids: fallback_reviewer_ids.map((id) => Number(id)) }
      : {}),
    ...(filteredFallbackEmployeeSegments && filteredFallbackEmployeeSegments.length > 0
      ? { fallback_employee_segments: filteredFallbackEmployeeSegments }
      : {}),
  }
}
